import { Popup } from '@progress/kendo-react-popup'
import Translation from 'components/Translation'
import { useAuth } from 'context/AuthContext'
import { useTenantContext } from 'context/TenantContext'
import Link from 'next/link'
import Router from 'next/router'
import { useEffect, useRef, useState } from 'react'
import { connectStripe } from 'services/integrations/stripe'
import { classNames } from 'utils/dom-helpers'
import { IntegrationCardProps } from '../types'

export function IntegrationsCard({
    key,
    integration,
    integration_location_id,
    location_id,
    refreshTenantIntegration
}: {
    key: string
    integration: IntegrationCardProps
    integration_location_id: string
    location_id: string
    refreshTenantIntegration(): void
}) {
    const { setAttributes, attributes } = useAuth()
    const { tenant } = useTenantContext()
    const params = new URLSearchParams(location.search)
    const state_params: Record<string, string> = {}
    const icon_ref = useRef<HTMLInputElement>(null)
    const [show_info, setShowInfo] = useState<boolean>(false)

    params.get('state') &&
        params
            .get('state')
            ?.split('&')
            .map((pair: string) => {
                const [key, val] = pair.split('=')
                state_params[key] = val
            })

    const [integration_id, activateIntegration] = useState<string>(
        state_params.integration_id
    )

    useEffect(() => {
        if (integration_id == integration.id) {
            activateIntegration('')
            Router.push({ query: null })

            connectStripe({
                stripe_code: params.get('code') as string,
                tenant_id: tenant?.id as string,
                integration_id,
                location_id
            })
                .then((r) => {
                    if (r && r.success) {
                        setAttributes({
                            ...attributes,
                            activated_integration: { name: integration.name, logo: integration.logo, id: integration.id },
                        })
                        refreshTenantIntegration()
                    }
                })
                .catch(console.error)
        }
    }, [integration_id])

    return (
        <div
            key={key}
            className={classNames(
                "p-8 shadow-2xl min-h-[240px] w-full flex flex-col justify-between",
                integration.name === 'XERO' ? "hidden" : ""
            )}>
            <div>
                <div className="flex justify-between">
                    {integration.logo ? (
                        <div
                            className="bg-no-repeat bg-contain w-16 h-16 block"
                            style={{ backgroundImage: `url(${integration.logo})` }}
                        />
                    ) : (
                        <strong>{integration.name}</strong>
                    )}
                    <div className={classNames(
                        integration_location_id === location_id
                            ? 'bg-red-200 text-red-700' :
                            integration.connected_account_id
                                ? 'bg-green-100 text-green-600'
                                : 'bg-gray-200 text-gray-600',
                        'flex items-center text-center font-semibold rounded-full px-3 h-8'
                    )}>
                        <i className={classNames("feather-map-pin mr-1 text-red-700",
                            integration_location_id !== location_id ? 'hidden' : 'visible'
                        )} />
                        <Translation
                            content_key={
                                integration.connected_account_id
                                    ? integration.business_email ||
                                    integration.business_name ||
                                    'connected'
                                    : 'not_connected'
                            }
                        />
                    </div>
                </div>
                <Translation
                    content_key={`${integration.name.toLowerCase()}_integration_description`}
                    className="text-gray-400 mt-5 pb-5 border-b-2 border-gray-200"
                    render_as="div"
                />
            </div>
            <div className='py-6 text-sm'>
                {integration.connected_account_id && !integration_location_id || !(integration_location_id === location_id) ? (
                    <Translation
                        content_key="connect_location_integration"
                        render_as="span"
                    />
                ) : (
                    <Translation
                        content_key="common.connect_location_integration"
                        render_as="span"
                    />
                )}
                <i className="feather-info text-blue-500 ml-2"
                    ref={icon_ref}
                    onMouseEnter={() => {
                        setShowInfo(true)
                    }}
                    onMouseLeave={() => {
                        setShowInfo(false)
                    }}
                />
                <Popup
                    anchor={icon_ref.current}
                    show={show_info}
                    animate={false}
                    popupAlign={{
                        horizontal: 'left',
                        vertical: 'top',
                    }}
                    popupClass={
                        'bg-black text-white text-xs shadow-lg rounded-xl w-60 px-2 py-1 mt-2'
                    }
                    className="z-[102]"
                >
                    <Translation
                        render_as="p"
                        content_key="common.info_loc_spec_integration"
                    />
                </Popup>
            </div>
            {integration.connected_account_id ? (
                integration_location_id && (integration_location_id === location_id) ? (
                    <div>
                        <button
                            type="button"
                            className="h-10 rounded-lg border-slate-800/20 text-primary border px-5"
                            onClick={() => {
                                setAttributes({
                                    ...attributes,
                                    to_disconnect: integration.tenant_integration_id as string,
                                    connected_account_id: integration.connected_account_id as string,
                                    logo: integration.logo,
                                    name: integration.name,
                                })
                            }}
                        >
                            <Translation content_key="common.disconnect" />
                        </button>
                    </div>
                ) : !integration_location_id || !(integration_location_id === location_id) ? (
                    <div>
                        <Link href={integration.connect_url as string}>
                            <a className="h-10 flex justify-center items-center gap-2 w-48 rounded-lg border-slate-800/20 text-primary border px-5">
                                <Translation
                                    content_key="connect_another"
                                    render_as="span"
                                />
                            </a>
                        </Link>
                    </div>
                ) : ''
            ) : (
                <div className="flex">
                    <Link href="/dashboard/integrations">
                        <a className="h-10 rounded-lg border-slate-800/20 flex items-center justify-center text-primary border px-5 gap-3 mt-5">
                            <Translation
                                content_key="setup_integration"
                                render_as="span"
                            />
                        </a>
                    </Link>
                </div>
            )}
        </div>
    )
}
